// new structure
import React, { useEffect, useRef, useState } from "react";
import {
  DropDownIcon,
  headerCloseIcon,
  headerOpenIcon,
  NotificationIcon,
} from "../assets/icons";
import { Link, useNavigate } from "react-router-dom";
import userProfile from "../assets/images/noProfile.png";
import { useSelector } from "react-redux";

const Header = ({ toggleSidebar, isOpen }) => {
  const [open, setOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const dropdownRef = useRef(null);
  const notificationRef = useRef(null);
  const user = useSelector((state) => state.admins.user);

  const navigate = useNavigate();

  // Sample notification data
  const notifications = [
    {
      id: 1,
      title: "New Leave Request",
      message: "John Smith has submitted a leave request for Feb 1-5",
      time: "2024-01-15 10:30AM",
      priority: "High",
      priorityColor: "bg-[#FFE4D7] text-[#F45C0C]",
    },
    {
      id: 2,
      title: "Timesheet Reminder",
      message: "Please submit your timesheet for the week ending Jan 12",
      time: "2024-01-15 10:30AM",
      priority: "Under review",
      priorityColor: "bg-[#E3EDFF] text-[#4285F4]",
    },
  ];

  // Close dropdown and notification slider on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        setNotificationOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  return (
    <header className="sticky w-full top-0 z-50 border-b-[1px] border-solid border-[#EBEBEB] h-16 bg-[#F6F6F6] shadow flex items-center justify-between px-2 sm:px-4 gap-2">
      <div className="flex items-center gap-2">
        <button
          onClick={toggleSidebar}
          className="border-none outline-none lg:hidden block"
        >
          {isOpen ? headerCloseIcon : headerOpenIcon}
        </button>
        <h1 className="text-h2 Rubik !font-bold mb-0">Overview</h1>
      </div>
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-3">
          <div className="relative inline-block text-left" ref={dropdownRef}>
            <button
              onClick={() => setOpen(!open)}
              className="inline-flex w-full items-center justify-center gap-x-2 sm:gap-x-3 rounded-md ring-1 ring-inset ring-white/5 hover:bg-white/20"
            >
              <img
                src={userProfile}
                alt="User"
                className="h-7 sm:h-10 w-7 sm:w-10 rounded-full bg-[#E7E5FF] flex-none"
              />
              <div className="flex flex-col text-left gap-1 hidden sm:block">
                <div className="!font-semibold text-black text-sub_span">
                  {user.name}
                </div>
                <div className="text-body !font-normal text-gray">
                  {user.email}
                </div>
              </div>
              {DropDownIcon}
            </button>

            {/* Dropdown Menu */}
            {open && (
              <div className="z-99999 absolute right-0 mt-2 w-56 origin-top-right rounded-md bg-yellow shadow-lg ring-1 ring-black ring-opacity-5">
                <div className="py-1">
                  <Link
                    to="/profile"
                    className="block px-4 py-2 text-sub_span text-blue-100 !font-medium"
                  >
                    Account settings
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
        <div>
          <div
            className="relative inline-block cursor-pointer"
            onClick={() => setNotificationOpen(!notificationOpen)}
          >
            {NotificationIcon}
            <span className="absolute top-0 right-0 h-[10px] w-[10px] rounded-full bg-blue border-[2.5px] border-yellow flex items-center justify-center"></span>
          </div>
        </div>
      </div>

      {/* Notification Slider (always mounted; visibility via classes) */}
      <div
        className={`fixed inset-0 z-[9999] transition-opacity duration-300 ${
          notificationOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
      >
        {/* Backdrop */}
        <button
          aria-label="Close notifications"
          onClick={() => setNotificationOpen(false)}
          className={`absolute inset-0 transition-opacity duration-300 bg-black/20 backdrop-blur-sm`}
        ></button>

        {/* Slider */}
        <div
          ref={notificationRef}
          className={`absolute right-0 top-0 h-full max-w-[672px] w-full bg-white shadow-2xl transform transition-transform duration-300 ${
            notificationOpen ? "translate-x-0" : "translate-x-[100%]"
          }`}
        >
          {/* Header */}
          <div className="bg-blue-500 text-white p-4 flex items-center justify-between">
            <h2
              className="text-xl font-semibold m-0"
              style={{ fontFamily: "Rubik" }}
            >
              Notification
            </h2>
            <button
              onClick={() => setNotificationOpen(false)}
              className="text-white hover:text-gray-200 transition-colors w-[40px] h-[40px] flex items-center justify-center bg-[#F6F6F6] rounded-full"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="#646363"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Notifications List */}
          <div className="p-4 space-y-4 overflow-y-auto h-full pb-20 bg-[#fff]">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className="bg-[#FAFAFA] rounded-lg p-4 border border-[#F1F6FF]"
              >
                <div className="flex items-start justify-between mb-2">
                  <h3
                    className="font-semibold text-gray-900 text-xl mb-0"
                    style={{ fontFamily: "Rubik" }}
                  >
                    {notification.title}
                  </h3>
                  <span className={`color-label ${notification.priorityColor}`}>
                    {notification.priority}
                  </span>
                </div>
                <p className="text-gray-600 text-sm mb-2">
                  {notification.message}
                </p>
                <p className="text-gray-400 text-xs">{notification.time}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
